import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import FilterIcon from '@/fo/icons/base/icons/filter32.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitToggleBase } from '@/fo/ui/kit/toggle/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { BRANDS_FILTERS, FILTERS_SLUG } from '../../constants';
import { SelectedFilters } from '../../types';

import { styles } from './BrandFilter.styles';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface BrandFilterProps {
  selectedFilters: SelectedFilters;
  onChange: (slug: string, value: string, checked: boolean) => void;
  color?: Variants['color'];
  onClick?: NativeElement['onClick'];
  className?: string;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  isOpenModal?: boolean;
  onReset: () => void;
  onShowPromotions: () => void;
  promotionsCount: number;
  onToggleChange: (slug: string, value: string, checked: boolean) => void;
}

export function BrandFilter({
  selectedFilters,
  color,
  onChange,
  onOpenChange,
  onReset,
  onShowPromotions,
  promotionsCount,
  isLoading,
  onToggleChange,
  isOpenModal
}: BrandFilterProps) {
  const t = useTranslations();
  const slots = styles({ color });
  const [isOpen, setIsOpen] = useState(false);
  const selectedFiltersBySlug = selectedFilters[FILTERS_SLUG.BRAND_NAME];
  const isDesktop = useMediaQuery('md');

  const openDrawer = (open: boolean) => {
    if (open) {
      setIsOpen(true);
    }
    onOpenChange?.(open);
  };

  useEffect(() => {
    if (!isOpenModal && isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  useEffect(() => {
    if (isDesktop && isOpen) {
      setIsOpen(false);
      onOpenChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const trigger = (
    <UiKitButtonBase
      className={slots.filterButton()}
      childrenClassName={slots.filterButtonChildren()}
      color="ghost"
    >
      <FilterIcon className={slots.filterIcon()} />
      {t('filters')}
    </UiKitButtonBase>
  );

  return (
    <>
      <UiKitDrawerBase
        title={t('filters')}
        direction="left"
        trigger={trigger}
        contentClassName={slots.drawer()}
        withoutMobile
        onOpenChange={openDrawer}
        open={isOpen}
      >
        <div className={slots.drawerContent()}>
          <div className={slots.titleWrapper()}>
            <h2 className={slots.title()}>{t('filters')}</h2>
            {Object.keys(selectedFilters).length > 0 && (
              <UiKitButtonBase
                color="ghost"
                onClick={onReset}
                className={slots.resetButton()}
              >
                {t('reset')}
              </UiKitButtonBase>
            )}
          </div>
          <div className={slots.filters()}>
            <div className={slots.title()}>
              {t('product.characteristics.brand')}
            </div>
            {BRANDS_FILTERS.map((brand) => (
              <UiKitToggleBase
                key={brand.value}
                label={brand.label}
                checked={
                  Array.isArray(selectedFiltersBySlug) &&
                  selectedFiltersBySlug.some((value) => value === brand.value)
                }
                onChange={(checked) =>
                  onToggleChange(FILTERS_SLUG.BRAND_NAME, brand.value, checked)
                }
                checkedView="checkedBackground"
              />
            ))}
          </div>
          <div className={slots.footer()}>
            <UiKitButtonBase
              className={slots.showProductsButton()}
              onClick={onShowPromotions}
              disabled={promotionsCount === 0}
              loading={isLoading}
            >
              {t('productCatalog.showProductByCount', {
                count: promotionsCount
              })}
            </UiKitButtonBase>
          </div>
        </div>
      </UiKitDrawerBase>
      <div className={slots.base()}>
        <div className={slots.list()}>
          {BRANDS_FILTERS.map((brand) => (
            <UiKitToggleBase
              key={brand.value}
              label={brand.label}
              checked={
                Array.isArray(selectedFiltersBySlug) &&
                selectedFiltersBySlug.some((value) => value === brand.value)
              }
              onChange={(checked) =>
                onChange(FILTERS_SLUG.BRAND_NAME, brand.value, checked)
              }
              checkedView="checkedBackground"
              disabled={isLoading}
            />
          ))}
        </div>
      </div>
    </>
  );
}
